import React, { useState } from 'react';
import { Modal, Form, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { registerTool } from '../../../services/toolAssignmentService';
import { listToolCategories } from '../../../services/toolCategoryService';
import ToolPhotoInput from './ToolPhotoInput';
import LocationPicker from '../../../components/locations/LocationPicker';

// `soloAlta` (#798): quien registra sin ser admin — hoy el supervisor. Puede
// INSERTAR la herramienta y nada mas, asi que no se le ofrece lo que la base
// le va a rechazar: la foto (el bucket `inventory-images` no le acepta la
// subida, y guardar la ruta es un UPDATE sobre `tools`) ni el link a gestionar
// ubicaciones (`/admin/locations` es admin-only). Elige categoria y ubicacion
// entre las que YA existen.
const ToolRegisterModal = ({ show, onHide, soloAlta = false }) => {
  const queryClient = useQueryClient();
  const [serial, setSerial] = useState('');
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [error, setError] = useState('');

  const { data: catsData } = useQuery({
    queryKey: ['tool-categories'],
    queryFn: () => listToolCategories({ active_only: true }),
    enabled: show,
    staleTime: 5 * 60 * 1000
  });
  const categories = catsData?.data || [];

  const reset = () => {
    setSerial(''); setName(''); setCategoryId(''); setLocationId(''); setBrand('');
    setModel(''); setDescription(''); setNotes('');
    setImageFile(null); setImagePreview(null);
    setRequiresApproval(false); setError('');
  };

  const close = () => { reset(); onHide(); };

  const mutation = useMutation({
    mutationFn: () => registerTool({
      serial_number: serial,
      name,
      category_id: categoryId || null,
      location_id: locationId || null,
      brand: brand || null,
      model: model || null,
      description: description || null,
      notes: notes || null,
      imageFile: soloAlta ? null : imageFile,
      requires_approval: requiresApproval
    }),
    onSuccess: ({ data, error: err, imageError }) => {
      if (err) { setError(err.message); return; }
      if (imageError) {
        toast.warning(`Herramienta registrada, pero falló la foto: ${imageError.message}`);
      } else {
        toast.success(`Herramienta "${data?.name}" registrada.`);
      }
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      queryClient.invalidateQueries({ queryKey: ['all-tools'] });
      queryClient.invalidateQueries({ queryKey: ['tools-available'] });
      close();
    },
    onError: (e) => setError(e.message)
  });

  const canSubmit = serial.trim() && name.trim() && !mutation.isPending;

  return (
    <Modal show={show} onHide={close} centered size="lg" fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-tools me-2" />Registrar nueva herramienta
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Row>
          <Col md={3} className="d-flex flex-column align-items-center mb-3">
            <Form.Label className="w-100">Foto</Form.Label>
            {soloAlta ? (
              <Form.Text className="text-muted">
                La foto la agrega un administrador después de registrar la herramienta.
              </Form.Text>
            ) : (
            <ToolPhotoInput
              file={imageFile}
              previewUrl={imagePreview}
              onChange={(f, url) => { setImageFile(f); setImagePreview(url); }}
              onRemove={() => { setImageFile(null); setImagePreview(null); }}
              disabled={mutation.isPending}
            />
            )}
          </Col>
          <Col md={9}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    value={name} onChange={e => setName(e.target.value)}
                    placeholder="Taladro percutor"
                    disabled={mutation.isPending}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Serial <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    value={serial} onChange={e => setSerial(e.target.value)}
                    placeholder="SN-12345"
                    disabled={mutation.isPending}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Categoría</Form.Label>
                  <Form.Select
                    value={categoryId}
                    onChange={e => setCategoryId(e.target.value)}
                    disabled={mutation.isPending}
                  >
                    <option value="">Sin categoría</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.label_es}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    ¿No está? <Link to="/herramientas/categorias" target="_blank" rel="noopener">Pedísela al asistente</Link>
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Ubicación{' '}
                    <i
                      className="bi bi-info-circle text-muted small"
                      title="¿Dónde está esta herramienta?"
                    />
                  </Form.Label>
                  <LocationPicker
                    value={locationId}
                    onChange={setLocationId}
                    disabled={mutation.isPending}
                    placeholder="Sin ubicación"
                    showCount
                  />
                  {!soloAlta && (
                    <Form.Text className="text-muted">
                      <Link to="/admin/locations" target="_blank" rel="noopener">Gestionar ubicaciones</Link>
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Marca</Form.Label>
                  <Form.Control
                    value={brand} onChange={e => setBrand(e.target.value)}
                    placeholder="Bosch, DeWalt..."
                    disabled={mutation.isPending}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Modelo</Form.Label>
                  <Form.Control
                    value={model} onChange={e => setModel(e.target.value)}
                    placeholder="GSB-13RE..."
                    disabled={mutation.isPending}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Col>
        </Row>
        <Form.Group className="mb-3">
          <Form.Label>Descripción</Form.Label>
          <Form.Control
            as="textarea" rows={2}
            value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Características o uso típico..."
            disabled={mutation.isPending}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Notas internas</Form.Label>
          <Form.Control
            as="textarea" rows={2}
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Condición inicial, accesorios, dónde se compró..."
            disabled={mutation.isPending}
          />
        </Form.Group>

        <Form.Group className="border rounded p-3 bg-light">
          <Form.Check
            type="switch"
            id="register-tool-requires-approval"
            label={
              <span>
                <i className="bi bi-shield-check me-1 text-warning" />
                <strong>Requiere aprobación para préstamo</strong>
              </span>
            }
            checked={requiresApproval}
            onChange={e => setRequiresApproval(e.target.checked)}
            disabled={mutation.isPending}
          />
          <Form.Text className="text-muted d-block mt-1">
            Si está activo, los técnicos no podrán tomar la herramienta
            directamente desde bodega. Tendrán que enviar una solicitud
            que un supervisor debe aprobar antes de la asignación.
            Recomendado para herramientas caras, calibradas o críticas.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close} disabled={mutation.isPending}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          disabled={!canSubmit}
          onClick={() => { setError(''); mutation.mutate(); }}
        >
          {mutation.isPending && <Spinner size="sm" animation="border" className="me-1" />}
          <i className="bi bi-check-lg me-1" />Registrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ToolRegisterModal;
